// © 2026 Camp Promethean, LLC. All Rights Reserved.
// Slim app-shell strip shown while a downgrade or cancellation is pending —
// no surprise lockouts: the user always sees the date and a one-click undo.

import { useNavigate } from "react-router-dom";
import { BrandMark } from "@/components/brand/BrandMark";
import { Button } from "@/components/ui/button";
import {
  formatPlanDate,
  usePlanChangeStatus,
} from "@/hooks/usePlanChangeStatus";

export function PendingPlanChangeBanner() {
  const navigate = useNavigate();
  const { status, loading, keeping, hasPendingChange, keepPlan } =
    usePlanChangeStatus();

  if (loading || !hasPendingChange) return null;

  const date = formatPlanDate(
    status.pendingChangeAt ?? status.currentPeriodEnd,
  );
  const headline = status.pendingTier
    ? `${status.tier} until ${date} — then ${status.pendingTier} begins.`
    : `${status.tier} until ${date} — then you move to Free.`;

  return (
    <div className="border-b border-border bg-card">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 md:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <BrandMark className="h-4 w-auto opacity-70" />
          <p className="min-w-0 text-sm text-muted-foreground">
            <span className="mr-2 hidden text-[10px] font-medium uppercase tracking-[0.16em] text-primary sm:inline">
              Plan change scheduled
            </span>
            {headline}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => keepPlan()}
            disabled={keeping}
          >
            {keeping ? "Restoring…" : "Keep my plan"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-muted-foreground"
            onClick={() => navigate("/profile")}
          >
            Manage
          </Button>
        </div>
      </div>
    </div>
  );
}
